import type { FontSpec, LegendElement, Plugin } from 'chart.js';
import { toFont } from 'chart.js/helpers';

export type CurrencyLegendPluginOptions = {
  text: string;
  gap?: number;
};

type LegendHitBox = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type LegendWithCurrency = LegendElement<'bar'> & {
  legendHitBoxes?: LegendHitBox[];
  lineWidths?: number[];

  currencyWrapped?: boolean;
  currencyFitPatched?: boolean;
  currencyOptions?: CurrencyLegendPluginOptions;
};

function measureTextWidth(
  ctx: CanvasRenderingContext2D,
  legend: LegendElement<'bar'>,
  text: string,
): number {
  const font = toFont(legend.options.labels.font as Partial<FontSpec>);

  ctx.save();
  ctx.font = font.string;
  const width = ctx.measureText(text).width;
  ctx.restore();

  return width;
}

export const currencyLegendPlugin: Plugin<'bar', CurrencyLegendPluginOptions> = {
  id: 'currencyLegend',

  beforeLayout(chart, _args, options) {
    const legend = chart.legend as LegendWithCurrency | undefined;

    if (!legend) {
      return;
    }

    legend.currencyOptions = options;

    if (legend.currencyFitPatched) {
      return;
    }

    legend.currencyFitPatched = true;

    const originalFit = legend.fit;

    legend.fit = function fit() {
      originalFit.call(this);

      const { text, gap = 16 } = this.currencyOptions ?? {};

      if (!text) {
        this.currencyWrapped = false;
        return;
      }

      const textWidth = measureTextWidth(chart.ctx, this, text);
      const lastLineWidth = this.lineWidths?.at(-1) ?? 0;

      const availableRightSpace = (() => {
        switch (this.options.align) {
          case 'start':
            return this.width - lastLineWidth;

          case 'end':
            return 0;

          default:
            return (this.width - lastLineWidth) / 2;
        }
      })();

      this.currencyWrapped = availableRightSpace < textWidth + gap;

      if (this.currencyWrapped) {
        const font = toFont(this.options.labels.font as Partial<FontSpec>);

        this.height += gap + font.lineHeight;
      }
    };
  },

  afterDraw(chart, _args, options) {
    if (!options.text) {
      return;
    }

    const legend = chart.legend as LegendWithCurrency | undefined;
    const hitBoxes = legend?.legendHitBoxes;

    if (!legend || !hitBoxes?.length) {
      return;
    }

    const lastHitBox = hitBoxes.at(-1);

    if (!lastHitBox) {
      return;
    }

    const { ctx } = chart;
    const font = toFont(legend.options.labels.font as Partial<FontSpec>);

    const gap = options.gap ?? 16;

    ctx.save();

    ctx.font = font.string;
    ctx.fillStyle = String(legend.options.labels.color);
    ctx.textBaseline = 'middle';

    if (legend.currencyWrapped) {
      ctx.textAlign = 'center';

      ctx.fillText(
        options.text,
        legend.left + legend.width / 2,
        lastHitBox.top + lastHitBox.height + gap + font.lineHeight / 2,
      );
    } else {
      ctx.textAlign = 'left';

      ctx.fillText(
        options.text,
        lastHitBox.left + lastHitBox.width + gap,
        lastHitBox.top + lastHitBox.height / 2,
      );
    }

    ctx.restore();
  },
};
